import { CONSENT_REVISION } from "./consent";
import {protectReplayFields,replayPageAllowed} from './replay';

type View = Window & typeof globalThis;
export interface VendorConfig { ga4: string | null; metrica: number | null; revision: string; replay?: boolean }

export function vendorConfig(hostname: string, env = import.meta.env): VendorConfig {
  const enabled = env.VITE_BB_ANALYTICS_VENDORS_ENABLED === "true";
  const com = env.MODE !== "ru" && ["blenderbridge.com", "www.blenderbridge.com"].includes(hostname);
  const ru = ["blenderbridge.ru", "www.blenderbridge.ru"].includes(hostname);
  const ga4 = enabled && com && /^G-[A-Z0-9]+$/.test(env.VITE_GA4_MEASUREMENT_ID ?? "") ? env.VITE_GA4_MEASUREMENT_ID! : null;
  const metrica = enabled && ru && /^[1-9][0-9]{0,11}$/.test(env.VITE_METRICA_COUNTER_ID ?? "") ? Number(env.VITE_METRICA_COUNTER_ID) : null;
  const replay=Boolean(metrica&&env.VITE_METRICA_REPLAY_ENABLED==='true');
  return { ga4, metrica, replay, revision: `${CONSENT_REVISION}${ga4 ? `:ga4:${ga4}` : ""}${metrica ? `:metrica:${metrica}` : ""}${replay?':webvisor':''}` };
}

interface VendorWindow extends Window {
  dataLayer?: unknown[];
  bbMetricaEcommerce?: unknown[];
  gtag?: (...args: unknown[]) => void;
  ym?: ((...args: unknown[]) => void) & { a?: unknown[][]; l?: number };
}

export function vendorPageContext(href:string, referrer:string):{page:string;referrer:string} {
  const original=new URL(href),page=new URL(original.pathname,original.origin);
  for(const key of ['utm_source','utm_medium','utm_campaign','utm_content','utm_term']) {
    const value=original.searchParams.get(key);
    if(value && /^[\p{L}\p{N} ._-]{1,64}$/u.test(value))page.searchParams.set(key,value);
  }
  let origin='';
  try {const source=new URL(referrer);if(['https:','http:'].includes(source.protocol)&&source.origin!==original.origin)origin=source.origin+'/';} catch { /* No referrer. */ }
  return {page:page.href,referrer:origin};
}

// Called only after affirmative consent, never as an advanced/denied-mode ping.
export function startVendors(view: View, config: VendorConfig): () => void {
  const target = view as VendorWindow;
  const scripts: HTMLScriptElement[] = [];
  let stopProtection=()=>{};
  const add = (src: string): void => {
    const script = view.document.createElement("script");
    script.async = true; script.src = src; script.referrerPolicy = "no-referrer";
    script.dataset.bbAnalyticsVendor = "true";
    view.document.head.append(script); scripts.push(script);
  };
  const context=vendorPageContext(view.location.href,view.document.referrer);
  const page=context.page;
  if (config.ga4) {
    target.dataLayer = [];
    target.gtag = function () { target.dataLayer!.push(arguments); };
    target.gtag("consent", "default", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
    target.gtag("consent", "update", { analytics_storage: "granted" });
    target.gtag("js", new Date());
    target.gtag("config", config.ga4, { send_page_view: false, allow_google_signals: false,
      allow_ad_personalization_signals: false, page_location: page, page_referrer: context.referrer, cookie_expires: 30 * 86400 });
    target.gtag("event", "page_view", { page_location: page, page_referrer: context.referrer, page_title: "Blender Bridge" });
    add(`https://www.googletagmanager.com/gtag/js?id=${config.ga4}`);
  }
  if (config.metrica) {
    target.bbMetricaEcommerce = [];
    let replay=Boolean(config.replay&&replayPageAllowed(view.location.href));
    if(replay){try{stopProtection=protectReplayFields(view);}catch{replay=false;}}
    const queue: unknown[][] = [];
    const ym = (...args: unknown[]): void => { queue.push(args); };
    target.ym = Object.assign(ym, { a: queue, l: Date.now() });
    target.ym(config.metrica, "init", { defer: true, clickmap: replay, trackLinks: false,
      accurateTrackBounce: true, webvisor: replay, trackHash: false, ecommerce: 'bbMetricaEcommerce', disableYtm: true });
    target.ym(config.metrica, "hit", page, { title: "Blender Bridge", referer: context.referrer });
    add("https://mc.yandex.ru/metrika/tag.js");
  }
  return () => {
    if (config.ga4) (view as unknown as Record<string, unknown>)[`ga-disable-${config.ga4}`] = true;
    if (config.metrica) target.ym?.(config.metrica, "destruct");
    stopProtection();
    for (const script of scripts) script.remove();
  };
}

export function clearVendorCookies(view: View): void {
  const host = view.location.hostname;
  const domains = ["", host, `.${host}`, ".blenderbridge.com", ".blenderbridge.ru"];
  for (const pair of view.document.cookie.split(";")) {
    const name = pair.split("=")[0]?.trim();
    if (!name || !/^(_ga(?:_|$)|_gid$|_gat|_ym_)/.test(name)) continue;
    for (const domain of domains) view.document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ""}`;
  }
}
